import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, Eye, EyeOff, Trash2, Edit2, ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const CaseStudies = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '', slug: '', client_name: '', industry: '', challenge: '', solution: '', results: '',
    testimonial_quote: '', testimonial_author: '', featured_image: '', tags: '', status: 'draft'
  });

  const fetchData = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/cms/case-studies`, { params, withCredentials: true });
      setCaseStudies(response.data);
    } catch (error) {
      toast.error("Failed to fetch case studies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        slug: formData.slug || generateSlug(formData.title),
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };
      
      if (editingItem) {
        await axios.put(`${API}/cms/case-studies/${editingItem.case_study_id}`, data, { withCredentials: true });
        toast.success("Case study updated");
      } else {
        await axios.post(`${API}/cms/case-studies`, data, { withCredentials: true });
        toast.success("Case study created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save case study");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '', slug: item.slug || '', client_name: item.client_name || '',
      industry: item.industry || '', challenge: item.challenge || '', solution: item.solution || '',
      results: item.results || '', testimonial_quote: item.testimonial_quote || '',
      testimonial_author: item.testimonial_author || '', featured_image: item.featured_image || '',
      tags: item.tags?.join(', ') || '', status: item.status || 'draft'
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    try {
      await axios.delete(`${API}/cms/case-studies/${id}`, { withCredentials: true });
      toast.success("Case study deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/cms/case-studies/${item.case_study_id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Case study ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ title: '', slug: '', client_name: '', industry: '', challenge: '', solution: '', results: '',
      testimonial_quote: '', testimonial_author: '', featured_image: '', tags: '', status: 'draft' });
  };

  const statusColors = {
    draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    published: 'bg-green-500/20 text-green-400 border-green-500/30',
    archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  const filteredItems = caseStudies.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title="Case Studies">
      <div className="space-y-6" data-testid="admin-case-studies">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Case Studies</h2>
            <p className="text-sm text-[#8A8A8A]">Portfolio content for public website</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Case Study
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingItem ? 'Edit' : 'New'} Case Study</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Title *" required value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})} className="input-field" />
                  <input type="text" placeholder="URL Slug" value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})} className="input-field" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Client Name *" required value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})} className="input-field" />
                  <input type="text" placeholder="Industry" value={formData.industry}
                    onChange={(e) => setFormData({...formData, industry: e.target.value})} className="input-field" />
                </div>
                <textarea placeholder="Challenge *" required rows={3} value={formData.challenge}
                  onChange={(e) => setFormData({...formData, challenge: e.target.value})} className="input-field resize-none" />
                <textarea placeholder="Solution *" required rows={3} value={formData.solution}
                  onChange={(e) => setFormData({...formData, solution: e.target.value})} className="input-field resize-none" />
                <textarea placeholder="Results *" required rows={3} value={formData.results}
                  onChange={(e) => setFormData({...formData, results: e.target.value})} className="input-field resize-none" />
                <textarea placeholder="Testimonial Quote" rows={2} value={formData.testimonial_quote}
                  onChange={(e) => setFormData({...formData, testimonial_quote: e.target.value})} className="input-field resize-none" />
                <input type="text" placeholder="Testimonial Author" value={formData.testimonial_author}
                  onChange={(e) => setFormData({...formData, testimonial_author: e.target.value})} className="input-field" />
                <input type="text" placeholder="Featured Image URL" value={formData.featured_image}
                  onChange={(e) => setFormData({...formData, featured_image: e.target.value})} className="input-field" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Tags (comma-separated)" value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})} className="input-field" />
                  <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="input-field">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">{editingItem ? 'Update' : 'Create'} Case Study</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
            <input type="text" placeholder="Search case studies..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="input-field pl-10" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field w-full sm:w-48">
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#1C1C1C] border border-[#2E2E2E] h-48 animate-pulse" />
            ))
          ) : filteredItems.length === 0 ? (
            <div className="col-span-full text-center py-12 text-[#8A8A8A] border border-[#2E2E2E]">
              No case studies found
            </div>
          ) : (
            filteredItems.map((item) => (
              <div key={item.case_study_id} className="bg-[#1C1C1C] border border-[#2E2E2E] overflow-hidden group hover:border-[#5F8FB8]/30 transition-colors">
                {item.featured_image && (
                  <div className="h-32 bg-[#0E0E0E] overflow-hidden">
                    <img src={item.featured_image} alt={item.title} className="w-full h-full object-cover opacity-70" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-bold truncate">{item.title}</h3>
                      <p className="text-sm text-[#8A8A8A]">{item.client_name}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 hover:bg-[#2E2E2E]"><MoreVertical className="w-4 h-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                        <DropdownMenuItem onClick={() => handleEdit(item)} className="cursor-pointer hover:bg-[#2E2E2E]">
                          <Edit2 className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleStatus(item)} className="cursor-pointer hover:bg-[#2E2E2E]">
                          {item.status === 'published' ? <><EyeOff className="w-4 h-4 mr-2" /> Unpublish</> : <><Eye className="w-4 h-4 mr-2" /> Publish</>}
                        </DropdownMenuItem>
                        {item.status === 'published' && (
                          <DropdownMenuItem asChild className="cursor-pointer hover:bg-[#2E2E2E]">
                            <a href={`/work/${item.slug}`} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 mr-2" /> View Live
                            </a>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDelete(item.case_study_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <span className={`px-2 py-0.5 text-xs border ${statusColors[item.status]}`}>{item.status}</span>
                    {item.industry && <span className="text-xs text-[#8A8A8A]">{item.industry}</span>}
                  </div>
                  {item.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-[#2E2E2E] text-xs">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default CaseStudies;
